import { APIError, useClientPortalAPI, useSubmit } from "@certn/api";
import {
  cssToken,
  initialAddressValues,
  showToast,
  Text,
} from "@certn/cascadia";
import {
  Grid,
  PrimaryButton,
  Card,
  Divider,
  AddressFormFormikField,
  TextInputFormikField,
  ButtonGroup,
  SelectFormikField,
  SelectItem,
} from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { Form, Formik } from "formik";
import { t } from "i18next";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useState } from "react";
import { useParams } from "react-router-dom";

import { useDetailsSchema } from "./GroupDetailsSchema";
import { PermissiblePurposeSelect } from "../../../../components/permissible-purpose/PermissiblePurposeSelect";
import { useCanUpdateGroups } from "../../../../components/permissions/Permissions";
import { useClientAccount } from "../../../../hooks/useClientAccount";
import { useBillingProfilesCompact } from "../../../../hooks/useBillingProfilesCompact";
import { BillingProfileWhatsThis } from "../../../billing-profiles/components/BillingProfileWhatsThis";

interface Props {
  setHasUnsavedChanges: (arg: boolean) => void;
}

export function GroupDetailsTab(props: Props) {
  const { setHasUnsavedChanges } = props;
  const { client } = useClientAccount();
  const { groupId } = useParams();
  const { moveUsagesToNewBillingProfile } = useFlags();
  const [isBillingProfileChanged, setIsBillingProfileChanged] = useState(false);

  const schema = useDetailsSchema(isBillingProfileChanged);
  const [formHasUnsavedChanges, setFormHasUnsavedChanges] =
    useState<boolean>(false);

  const { isSubmitting, submit } = useSubmit(
    `/api/internal/client-portal/customer-groups/${groupId}/update/`,
    { throwErrors: true }
  );

  const { data: billingProfiles, isLoading: isbillingProfilesLoading } =
    useBillingProfilesCompact(client?.id, {
      ordering: "profile_name",
    });

  const { data: group, mutate: mutateGroup } = useClientPortalAPI(
    `/api/internal/client-portal/customer-groups/${groupId}/`
  );
  const isReadOnly = !useCanUpdateGroups() || !group?.is_active;

  const initialValues = {
    name: group?.name,
    applicant_facing_name: group?.applicant_facing_name,
    billing_profile: group?.billing_profile?.id,
    permissible_purpose: group?.permissible_purpose,
    address: {
      ...(group?.address ? group?.address : initialAddressValues),
    },
    move_uninvoiced_usages: undefined,
  };

  const filteredBillingProfiles = billingProfiles?.filter(
    (profile) => !profile.archived || profile.id === group?.billing_profile?.id
  );

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={schema}
      onSubmit={async (values, helpers) => {
        try {
          const transformedValues = {
            ...values,
            move_uninvoiced_usages: values.move_uninvoiced_usages === "true",
          };

          await submit(transformedValues);
          showToast(t("Group updated successfully"), { type: "success" });
          await mutateGroup();
          helpers.resetForm({
            values: { ...transformedValues, move_uninvoiced_usages: undefined },
          });
          setIsBillingProfileChanged(false);
        } catch (e) {
          console.error(e);
          showToast(t("Something went wrong, try again"), { type: "error" });
          if (e instanceof APIError) {
            helpers.setErrors(e.body as object);
          }
        }
      }}
    >
      {({ dirty, values }) => {
        if (dirty !== formHasUnsavedChanges) {
          setHasUnsavedChanges(true);
          setFormHasUnsavedChanges(dirty);
        }
        if (formHasUnsavedChanges && dirty === false) {
          setHasUnsavedChanges(false);
          setFormHasUnsavedChanges(dirty);
        }

        const isNewBillingProfileChanged =
          values.billing_profile !== group?.billing_profile?.id;
        if (isNewBillingProfileChanged) setIsBillingProfileChanged(true);

        return (
          <Form>
            <Card
              className={css({
                padding: cssToken("space-5"),
                borderRadius: cssToken("border-radius-3"),
              })}
            >
              <Grid form>
                <Text style="form/section-heading">{t("Group details")}</Text>
                <TextInputFormikField
                  testID="group-name"
                  name="name"
                  label={t("Group name")}
                  isRequired
                  isDisabled={isReadOnly}
                />
                <TextInputFormikField
                  testID="facing-name"
                  name="applicant_facing_name"
                  label={t("Applicant facing name")}
                  isRequired
                  isDisabled={isReadOnly}
                />
                <SelectFormikField
                  testID="billing-profile-select"
                  name="billing_profile"
                  label={t("Billing profile")}
                  isRequired
                  isLoading={isbillingProfilesLoading}
                  isDisabled={isReadOnly}
                  info={<BillingProfileWhatsThis />}
                >
                  {filteredBillingProfiles?.map((profile) => (
                    <SelectItem key={profile.id} id={profile.id}>
                      {profile.profile_name}
                    </SelectItem>
                  ))}
                </SelectFormikField>
                {moveUsagesToNewBillingProfile && isBillingProfileChanged && (
                  <SelectFormikField
                    testID="what-to-do-with-uninvoiced-checks-select"
                    name="move_uninvoiced_usages"
                    label={t("Move uninvoiced checks")}
                    isRequired
                    isLoading={isbillingProfilesLoading}
                    isDisabled={isReadOnly}
                    description={t(
                      "You changed billing profiles, what would you like to do with your uninvoiced checks?"
                    )}
                  >
                    <SelectItem id="true">
                      {t("Move all un-invoiced checks to new billing profile")}
                    </SelectItem>
                    <SelectItem id="false">
                      {t("Do not change existing checks' billing profile")}
                    </SelectItem>
                  </SelectFormikField>
                )}
                <PermissiblePurposeSelect
                  name="permissible_purpose"
                  label={t("Permissible purpose")}
                  testID="permissible-purpose-select"
                  isRequired
                  showInfo
                  isDisabled // always disabled after group creation
                />
                <Divider />
                <Text style="form/section-heading">{t("Group address")}</Text>
                <AddressFormFormikField
                  name="address"
                  isDisabled={isReadOnly}
                />
                {!isReadOnly && (
                  <ButtonGroup>
                    <PrimaryButton
                      type="submit"
                      size="medium"
                      testID="save-user-button"
                      isLoading={isSubmitting}
                      isDisabled={!dirty}
                    >
                      {t("Save")}
                    </PrimaryButton>
                  </ButtonGroup>
                )}
              </Grid>
            </Card>
          </Form>
        );
      }}
    </Formik>
  );
}
